"use client"

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { format, addDays } from 'date-fns';
import { CalendarIcon, Loader2, Mail, MessageSquare, Clock, ChevronRight, ChevronLeft, Send, Eye, Code, Paperclip, X, FileText, Image, File, PenSquare } from 'lucide-react';
import CampaignService from '@/api/campaign.service';
import CustomerService from '@/api/customers.service';
import SignatureService from '@/api/signature.service';
import { cn } from '@/lib/utils';
import TemplateSelector from '@/components/campaigns/TemplateSelector';
import CampaignPreview from '@/components/campaigns/CampaignPreview';
import VisualEditor from '@/components/campaigns/editor/VisualEditor';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { HtmlPreview } from '@/components/ui/html-preview';

interface CreateCampaignFormProps {
  businessId: number;
  onSuccess: () => void;
}

export default function CreateCampaignForm({ businessId, onSuccess }: CreateCampaignFormProps) {
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingCustomers, setIsFetchingCustomers] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const { toast } = useToast();
  const [editorMode, setEditorMode] = useState<'html' | 'visual'>('html');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  
  // Step 1 state
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState('email');
  const [description, setDescription] = useState('');
  
  // Step 2 state
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  
  // Step 3 state
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [scheduleType, setScheduleType] = useState('draft');
  const [scheduledDate, setScheduledDate] = useState<Date>(addDays(new Date(), 1));
  const [attachments, setAttachments] = useState<File[]>([]);
  
  // Signature state
  const [signatures, setSignatures] = useState<any[]>([]);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(null);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [isLoadingSignatures, setIsLoadingSignatures] = useState(false);
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState<any>(null);
  
  // Validation states
  const [step1Errors, setStep1Errors] = useState<{
    name?: string;
    type?: string;
  }>({});
  
  const [step2Errors, setStep2Errors] = useState<{
    selectedCustomers?: string;
  }>({});
  
  const [step3Errors, setStep3Errors] = useState<{
    subject?: string;
    content?: string;
    scheduleType?: string;
  }>({});
  
  const [previewImage, setPreviewImage] = useState<{url: string, name: string} | null>(null);
  
  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });
  
  useEffect(() => {
    const fetchCustomers = async () => {
      setIsFetchingCustomers(true);
      try {
        const response = await CustomerService.getAllCustomers(businessId);
        if (response.data.result === 'success') {
          setCustomers(response.data.customers || []);
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load customers",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast({
          title: "Error",
          description: "Failed to load customers.",
          variant: "destructive",
        });
      } finally {
        setIsFetchingCustomers(false);
      }
    };
    
    if (step === 2) {
      fetchCustomers();
    }
  }, [step, businessId, toast]);
  
  useEffect(() => {
    // Fetch signatures for this business
    const fetchSignatures = async () => {
      if (!selectedBusiness?.id) return;
      
      setIsLoadingSignatures(true);
      try {
        const response = await SignatureService.getAllSignaturesForSelf();
        
        if (response.data.result === 'success') {
          setSignatures(response.data.userSignatures || []);
          
          // Find and select default signature if exists
          const defaultSignature = response.data.userSignatures?.find((sig: any) => sig.isDefault);
          if (defaultSignature) {
            setSelectedSignatureId(defaultSignature.publicId);
            // Fetch the signature preview if it's the default
            await fetchSignaturePreview(defaultSignature.publicId);
          }
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load signatures",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching signatures:", error);
        toast({
          title: "Error",
          description: "Failed to load signatures",
          variant: "destructive",
        });
      } finally {
        setIsLoadingSignatures(false);
      }
    };
    
    if (step === 3 && campaignType === 'email') {
      fetchSignatures();
    }
  }, [step, campaignType, selectedBusiness?.id, toast]);
  
  const fetchSignaturePreview = async (signatureId: string) => {
    try {
      const response = await SignatureService.getSignature(signatureId);
      
      if (response.data.result === 'success') {
        setSignaturePreview(response.data.userSignature);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to load signature preview",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching signature preview:", error);
      toast({
        title: "Error",
        description: "Failed to load signature preview",
        variant: "destructive",
      });
    }
  };
  
  const handleSignatureChange = async (signatureId: string) => {
    setSelectedSignatureId(signatureId);
    await fetchSignaturePreview(signatureId);
  };
  
  const handleIncludeSignatureChange = (checked: boolean) => {
    setIncludeSignature(checked);
    if (checked && !signaturePreview && selectedSignatureId) {
      fetchSignaturePreview(selectedSignatureId);
    }
  };
  
  const validateStepOne = () => {
    const errors: {name?: string; type?: string} = {};
    
    if (!campaignName) {
      errors.name = 'Campaign name is required';
    }
    
    if (!campaignType) {
      errors.type = 'Campaign type is required';
    }
    
    setStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const validateStepTwo = () => {
    const errors: {selectedCustomers?: string} = {};
    
    if (selectedCustomers.length === 0) {
      errors.selectedCustomers = 'At least one recipient is required';
    }
    
    setStep2Errors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const validateStepThree = () => {
    const errors: {subject?: string; content?: string; scheduleType?: string} = {};
    
    if (campaignType === 'email' && !subject) {
      errors.subject = 'Subject is required';
    }
    
    if (!content || content.length < 10) {
      errors.content = 'Content must be at least 10 characters';
    }
    
    if (!scheduleType) {
      errors.scheduleType = 'Schedule type is required';
    }
    
    setStep3Errors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const onStepOneNext = () => {
    if (validateStepOne()) {
      setStep(2);
    }
  };
  
  const onStepTwoNext = () => {
    if (validateStepTwo()) {
      setStep(3);
    }
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      // Convert FileList to Array and append to existing attachments
      const newFiles = Array.from(event.target.files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  const getFileIcon = (file: File) => {
    const fileType = file.type.split('/')[0];
    switch (fileType) {
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'application':
        if (file.type.includes('pdf')) {
          return <FileText className="w-4 h-4" />;
        }
        return <File className="w-4 h-4" />;
      default:
        return <File className="w-4 h-4" />;
    }
  };
  
  const isImageFile = (file: File) => {
    return file.type.startsWith('image/');
  };
  
  const handlePreviewImage = (file: File) => {
    if (isImageFile(file)) {
      setPreviewImage({
        url: URL.createObjectURL(file),
        name: file.name
      });
    }
  };
  
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  
  const onStepThreeSubmit = async () => {
    if (!validateStepThree()) return;
    
    setIsLoading(true);
    setShowUploadProgress(attachments.length > 0);
    setUploadProgress(0);
    
    try {
      // Use FormData if there are attachments
      let campaignData: any;
      
      if (attachments.length > 0 && campaignType === 'email') {
        campaignData = new FormData();
        campaignData.append('name', campaignName);
        campaignData.append('type', campaignType);
        campaignData.append('description', description || '');
        campaignData.append('subject', subject);
        campaignData.append('content', content);
        campaignData.append('status', scheduleType);
        
        // Add signature if selected
        if (campaignType === 'email' && includeSignature && selectedSignatureId) {
          campaignData.append('signatureId', selectedSignatureId);
        }
        
        // Add recipients as JSON string
        campaignData.append('recipients', JSON.stringify(selectedCustomers));
        
        // Add scheduled date if needed
        if (scheduleType === 'scheduled' && scheduledDate) {
          campaignData.append('scheduledAt', scheduledDate.toISOString());
        }
        
        // Add attachments
        attachments.forEach((file, index) => {
          campaignData.append('attachments', file);
        });
        
        const response = await CampaignService.createCampaign(businessId, campaignData, progressEvent => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        });
        
        if (response.data.result === 'success') {
          toast({
            title: "Success",
            description: "Campaign created successfully!",
          });
          onSuccess();
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to create campaign.",
            variant: "destructive",
          });
        }
      } else {
        // Use regular JSON if no attachments
        campaignData = {
          name: campaignName,
          type: campaignType,
          description: description || '',
          recipients: selectedCustomers,
          subject: subject,
          content: content,
          status: scheduleType,
        };
        
        // Add signature if selected
        if (campaignType === 'email' && includeSignature && selectedSignatureId) {
          campaignData.signatureId = selectedSignatureId;
        }
        
        if (scheduleType === 'scheduled' && scheduledDate) {
          campaignData.scheduledAt = scheduledDate;
        }
        
        const response = await CampaignService.createCampaign(businessId, campaignData);
        
        if (response.data.result === 'success') {
          toast({
            title: "Success",
            description: "Campaign created successfully!",
          });
          onSuccess();
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to create campaign.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast({
        title: "Error",
        description: "An error occurred while creating the campaign.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setShowUploadProgress(false);
    }
  };
  
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  const handleTemplateSelect = (template: any) => {
    if (campaignType === 'email') {
      setSubject(template.subject);
      setContent(template.htmlContent);
    } else {
      setContent(template.content);
    }
  };
  
  const handlePreview = () => {
    setShowPreview(true);
  };
  
  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCustomers(customers.map(c => c.publicId));
    } else {
      setSelectedCustomers([]);
    }
  };
  
  const handleToggleCustomer = (customerId: string, checked: boolean) => {
    if (checked) {
      setSelectedCustomers(prev => [...prev, customerId]);
    } else {
      setSelectedCustomers(prev => prev.filter(id => id !== customerId));
    }
  };
  
  // Sync content when changing between HTML and visual editor mode
  useEffect(() => {
    // This will ensure the content is properly synced when switching modes
    // The reset is handled inside the VisualEditor component
  }, [editorMode]);
  
  // Clean up object URLs when component unmounts or when previewImage changes
  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage.url);
      }
    };
  }, [previewImage]);
  
  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="space-y-2">
        <div className="flex justify-between">
          {[1, 2, 3].map((s) => (
            <div 
              key={s} 
              className={cn(
                "flex-1 h-2 rounded-full mx-1",
                s === step ? "bg-primary" : 
                s < step ? "bg-primary/50" : "bg-muted"
              )}
            />
          ))}
        </div>
        <div className="text-sm text-center text-muted-foreground">
          Step {step} of 3: {step === 1 ? 'Campaign Details' : step === 2 ? 'Select Recipients' : 'Compose Message'}
        </div>
      </div>
      
      {/* Step 1: Campaign Details */}
      {step === 1 && (
        <div className="space-y-6">
          <div>
            <Label htmlFor="campaign-name">Campaign Name</Label>
            <Input 
              id="campaign-name" 
              placeholder="Spring Sale Announcement" 
              value={campaignName} 
              onChange={(e) => setCampaignName(e.target.value)} 
            />
            {step1Errors.name && <p className="text-sm font-medium text-destructive mt-1">{step1Errors.name}</p>}
          </div>
          
          <div>
            <Label htmlFor="campaign-type">Campaign Type</Label>
            <Select value={campaignType} onValueChange={setCampaignType}>
              <SelectTrigger id="campaign-type">
                <SelectValue placeholder="Select campaign type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Email
                </SelectItem>
                <SelectItem value="sms" className="flex items-center gap-2 opacity-50" disabled>
                  <MessageSquare className="w-4 h-4 text-green-500" />
                  SMS
                  <span className="ml-auto text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Coming Soon</span>
                </SelectItem>
                <SelectItem value="whatsapp" className="flex items-center gap-2 opacity-50" disabled>
                  <MessageSquare className="w-4 h-4 text-green-600" />
                  WhatsApp
                  <span className="ml-auto text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Coming Soon</span>
                </SelectItem>
              </SelectContent>
            </Select>
            {step1Errors.type && <p className="text-sm font-medium text-destructive mt-1">{step1Errors.type}</p>}
          </div>
          
          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea 
              id="description"
              placeholder="Brief description of the campaign purpose..."
              className="resize-none"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          
          <div className="flex justify-end">
            <Button onClick={onStepOneNext} className="gap-1">
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Step 2: Select Recipients */}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <div className="mb-4">
              <Label>Select Recipients</Label>
            </div>
            
            {isFetchingCustomers ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="border rounded-md p-4 max-h-[300px] overflow-y-auto">
                {customers.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    No customers found
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3 p-2 bg-muted/30 rounded-md mb-2 sticky top-0 z-10">
                      <Checkbox
                        checked={selectedCustomers.length === customers.length}
                        onCheckedChange={handleToggleSelectAll}
                      />
                      <div className="font-medium text-sm">Select All ({customers.length})</div>
                    </div>
                    {customers.map((customer) => (
                      <div
                        key={customer.publicId}
                        className="flex flex-row items-start space-x-3 space-y-0 p-2 hover:bg-muted/50 rounded-md"
                      >
                        <Checkbox
                          checked={selectedCustomers.includes(customer.publicId)}
                          onCheckedChange={(checked) => handleToggleCustomer(customer.publicId, !!checked)}
                        />
                        <div className="space-y-1 leading-none">
                          <Label className="text-sm font-medium">
                            {customer.firstName} {customer.lastName}
                            {customer.companyName && ` (${customer.companyName})`}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {customer.emailAddress}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {step2Errors.selectedCustomers && <p className="text-sm font-medium text-destructive mt-1">{step2Errors.selectedCustomers}</p>}
          </div>
          
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={handleBack}
              className="gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            <Button onClick={onStepTwoNext} className="gap-1">
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Step 3: Compose Message */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="flex justify-end gap-2 mb-4">
            <TemplateSelector 
              campaignType={campaignType as 'email' | 'sms' | 'whatsapp'} 
              onSelectTemplate={handleTemplateSelect} 
            />
          </div>
          
          {campaignType === 'email' && (
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input 
                id="subject" 
                placeholder="Your Special Offer Inside" 
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              {step3Errors.subject && <p className="text-sm font-medium text-destructive mt-1">{step3Errors.subject}</p>}
            </div>
          )}
          
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label>Content</Label>
              {campaignType === 'email' && (
                <div className="flex items-center space-x-1 bg-muted rounded-md p-1">
                  <Button
                    variant={editorMode === 'visual' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setEditorMode('visual')}
                    type="button"
                    className="gap-1 text-xs"
                  >
                    <Eye className="h-3 w-3" />
                    Visual
                  </Button>
                  <Button
                    variant={editorMode === 'html' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setEditorMode('html')}
                    type="button"
                    className="gap-1 text-xs"
                  >
                    <Code className="h-3 w-3" />
                    HTML
                  </Button>
                </div>
              )}
            </div>
            
            {campaignType === 'email' && editorMode === 'visual' ? (
              <div className="border rounded-md min-h-[400px]">
                <VisualEditor
                  initialContent={content}
                  onContentChange={(newContent) => {
                    setContent(newContent);
                  }}
                  subject={subject}
                  onSubjectChange={(newSubject) => {
                    setSubject(newSubject);
                  }}
                  type={campaignType as 'email' | 'sms' | 'whatsapp'}
                />
              </div>
            ) : (
              <Textarea 
                placeholder="Write your message here..."
                className="resize-none min-h-[200px]"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            )}
            <div className="text-xs text-muted-foreground mt-1">
              Use <code className="bg-muted px-1 rounded">&#123;&#123;firstName&#125;&#125;</code>, <code className="bg-muted px-1 rounded">&#123;&#123;lastName&#125;&#125;</code>, etc. to include recipient data.
              Variables like <code className="bg-muted px-1 rounded">[[discount]]</code> in templates need manual input.
            </div>
            {step3Errors.content && <p className="text-sm font-medium text-destructive mt-1">{step3Errors.content}</p>}
          </div>
          
          {/* Attachments Section - Only show for email campaigns */}
          {campaignType === 'email' && (
            <div>
              <Label className="mb-2 block">Attachments</Label>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-1"
                  >
                    <Paperclip className="h-4 w-4" />
                    Add Attachment
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileChange}
                    multiple
                  />
                  <div className="text-xs text-muted-foreground">
                    Supported formats: PDF, Images, Documents, etc.
                  </div>
                </div>
                
                {/* Attachment List */}
                {attachments.length > 0 && (
                  <div className="border rounded-md p-3 space-y-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-muted/30 p-2 rounded-md">
                        <div className="flex items-center gap-2">
                          {getFileIcon(file)}
                          <div 
                            className={cn(
                              "text-sm truncate max-w-[200px]",
                              isImageFile(file) && "cursor-pointer hover:underline text-primary"
                            )}
                            onClick={() => isImageFile(file) && handlePreviewImage(file)}
                          >
                            {file.name}
                          </div>
                          <div className="text-xs text-muted-foreground">{formatFileSize(file.size)}</div>
                          {isImageFile(file) && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handlePreviewImage(file)} 
                              className="h-6 px-1 py-0 text-xs"
                            >
                              Preview
                            </Button>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeAttachment(index)} 
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Signatures Section - Only show for email campaigns */}
          {campaignType === 'email' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="mb-0">Email Signature</Label>
                {selectedSignatureId && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowSignaturePreview(!showSignaturePreview)}
                    className="gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    {showSignaturePreview ? "Hide" : "Show"} Preview
                  </Button>
                )}
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeSignature"
                    checked={includeSignature}
                    onCheckedChange={handleIncludeSignatureChange}
                  />
                  <Label htmlFor="includeSignature" className="cursor-pointer">
                    Include signature in email
                  </Label>
                </div>
                
                {includeSignature && (
                  <div className="flex-1">
                    <Select
                      value={selectedSignatureId || ''}
                      onValueChange={handleSignatureChange}
                      disabled={isLoadingSignatures || signatures.length === 0}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select signature" />
                      </SelectTrigger>
                      <SelectContent>
                        {isLoadingSignatures ? (
                          <div className="flex items-center justify-center p-2">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Loading signatures...
                          </div>
                        ) : signatures.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground">
                            No signatures found. Create one in Signatures section.
                          </div>
                        ) : (
                          signatures.map((signature: any) => (
                            <SelectItem 
                              key={signature.publicId} 
                              value={signature.publicId}
                              className="flex items-center gap-2"
                            >
                              <div className="flex items-center gap-2">
                                <PenSquare className="h-4 w-4 text-primary" />
                                {signature.name}
                                {signature.isDefault && (
                                  <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              {/* Signature Preview */}
              {showSignaturePreview && signaturePreview && includeSignature && (
                <div className="border rounded-md p-4 bg-gray-50 space-y-2">
                  <div className="text-sm font-medium mb-2 border-b pb-2">
                    Signature Preview
                  </div>
                  
                  {signaturePreview.signatureType === 'image' || signaturePreview.signatureType === 'both' ? (
                    <div className="mb-2">
                      {signaturePreview.imageLink && (
                        <img 
                          src={signaturePreview.imageLink} 
                          alt="Signature" 
                          className="max-h-24 object-contain"
                        />
                      )}
                    </div>
                  ) : null}
                  
                  {signaturePreview.signatureType === 'text' || signaturePreview.signatureType === 'both' ? (
                    <HtmlPreview html={signaturePreview.content} className="text-sm max-h-40 overflow-y-auto" />
                  ) : null}
                  
                  {signaturePreview.includeDisclaimer && signaturePreview.disclaimer && (
                    <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                      {signaturePreview.disclaimer}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div>
            <Label htmlFor="scheduleType">When to send</Label>
            <Select value={scheduleType} onValueChange={setScheduleType}>
              <SelectTrigger id="scheduleType">
                <SelectValue placeholder="Select when to send" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft" className="flex items-center gap-2">
                  Save as Draft
                </SelectItem>
                <SelectItem value="send" className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Now
                </SelectItem>
                <SelectItem value="scheduled" className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Schedule for Later
                </SelectItem>
              </SelectContent>
            </Select>
            {step3Errors.scheduleType && <p className="text-sm font-medium text-destructive mt-1">{step3Errors.scheduleType}</p>}
          </div>
          
          {scheduleType === 'scheduled' && (
            <div className="flex flex-col">
              <Label>Schedule Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full pl-3 text-left font-normal",
                      !scheduledDate && "text-muted-foreground"
                    )}
                  >
                    {scheduledDate ? (
                      format(scheduledDate, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={scheduledDate}
                    onSelect={(date) => date && setScheduledDate(date)}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
          
          {showUploadProgress && (
            <div className="space-y-1">
              <div className="text-sm flex justify-between">
                <span>Uploading attachments</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}
          
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={handleBack}
              className="gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handlePreview}
                className="gap-1"
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              <Button onClick={onStepThreeSubmit} disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {scheduleType === 'draft' ? 'Save Draft' : 
                 scheduleType === 'send' ? 'Send Now' : 'Schedule Campaign'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <CampaignPreview
            type={campaignType as 'email' | 'sms' | 'whatsapp'}
            subject={campaignType === 'email' ? subject : undefined}
            content={content}
            recipientCount={selectedCustomers.length}
            recipientIds={selectedCustomers}
            businessId={businessId}
            onClose={() => setShowPreview(false)}
            onContentChange={(newContent) => setContent(newContent)}
            onSubjectChange={(newSubject) => setSubject(newSubject)}
            signature={includeSignature && signaturePreview ? signaturePreview : null}
          />
        </div>
      )}
      
      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="mb-4">{previewImage?.name}</DialogTitle>
          {previewImage && (
            <div className="relative overflow-hidden rounded-md">
              <img
                src={previewImage.url}
                alt={previewImage.name}
                className="w-full h-auto object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
} 